import { Tooltip } from '@mui/joy';
import { isEmpty } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { parseString } from 'core/utils';
import { OTHER_DISCIPLINE } from 'corporate/constants';
import { IDiscipline } from 'corporate/types';

import { Field, withQueryState } from 'coreOld/components';
import { FormError, Icon } from 'coreOld/elements';
import { InputType } from 'coreOld/enums';
import { splitArray } from 'coreOld/utils';
import { Variant } from 'theme/enums';

import {
  CheckboxColumn,
  CheckboxWrapper,
  CheckboxesWrapper,
  DisciplinesWrapper,
  Label,
  Title,
  TooltipContent,
  TooltipIconWrapper,
} from './disciplineCheckboxesStyle';

interface IProps {
  disciplines: IDiscipline[];
  otherDiscipline: IDiscipline;
  setOtherDiscipline: Dispatch<SetStateAction<IDiscipline>>;
  otherDisciplineChecked: boolean;
  existingDisciplines?: { [disciplineId: number | string]: string };
  onChange: (checked: boolean, discipline: IDiscipline) => void;
  error?: string;
}

const DisciplineCheckboxesView = ({
  disciplines,
  otherDiscipline,
  setOtherDiscipline,
  otherDisciplineChecked,
  existingDisciplines,
  onChange,
  error,
}: IProps) => {
  const { t } = useTranslation();

  return (
    <DisciplinesWrapper>
      <Title>{t('labelDisciplineSP')}</Title>

      <Label>{t('messageChooseDisciplineSP')}</Label>

      {error && <FormError error={error} />}

      <CheckboxesWrapper>
        {splitArray(disciplines, 5).map((disciplinesRows, key) => (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <CheckboxColumn key={key}>
            {disciplinesRows.map((discipline: IDiscipline) => {
              const hasAssociation = discipline.id
                ? Boolean(existingDisciplines?.[discipline.id])
                : Boolean(existingDisciplines?.[OTHER_DISCIPLINE]);

              if (!discipline?.id) {
                return (
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <CheckboxWrapper key={discipline.name}>
                    <Field
                      type={InputType.CHECKBOX}
                      fullWidth={false}
                      inputProps={{
                        label: discipline.name,
                        name: discipline.name,
                        defaultChecked: otherDisciplineChecked,
                        disabled: hasAssociation || !otherDiscipline?.name,
                        onChange: (event) => onChange(event.target.checked, otherDiscipline),
                      }}
                    />
                    <Field
                      type={InputType.TEXTFIELD}
                      inputProps={{
                        placeholder: t('labelDisciplineName'),
                        defaultValue: otherDiscipline?.name,
                        disabled: otherDisciplineChecked,
                        editable: !otherDisciplineChecked,
                        onChange: (event) => setOtherDiscipline({ name: event?.target?.value }),
                      }}
                    />
                  </CheckboxWrapper>
                );
              }

              if (!isEmpty(discipline?.description)) {
                return (
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <CheckboxWrapper key={discipline.id}>
                    <Tooltip title={parseString(discipline?.description)} variant={Variant.SOLID}>
                      <TooltipContent>
                        <Field
                          type={InputType.CHECKBOX}
                          inputProps={{
                            label: discipline.name,
                            name: discipline.name,
                            defaultChecked: hasAssociation,
                            disabled: hasAssociation,
                            onChange: (event) => onChange(event.target.checked, discipline),
                          }}
                        />
                        <Icon name={Icon.INFO} wrapperStyle={TooltipIconWrapper} />
                      </TooltipContent>
                    </Tooltip>
                  </CheckboxWrapper>
                );
              }

              return (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                <CheckboxWrapper key={discipline.id}>
                  <Field
                    type={InputType.CHECKBOX}
                    inputProps={{
                      label: discipline.name,
                      name: discipline.name,
                      defaultChecked: hasAssociation,
                      disabled: hasAssociation,
                      onChange: (event) => onChange(event.target.checked, discipline),
                    }}
                  />
                </CheckboxWrapper>
              );
            })}
          </CheckboxColumn>
        ))}
      </CheckboxesWrapper>
    </DisciplinesWrapper>
  );
};

export default withQueryState(DisciplineCheckboxesView);
